import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCoins, faChartLine, faUsers } from '@fortawesome/free-solid-svg-icons';

interface RevenueDateList {
    date: string;
    revenue: number;
}

interface RevenueGraphDto {
    coin: string;
    data: RevenueDateList[];
}

interface CombinedData {
    date: string;
    [key: string]: any;
}

interface CoinRevenueDto {
    coin: string;
    revenue: number;
}

interface RevenueDto {
    user: string;
    coinRevenue: CoinRevenueDto[];
}

const RevenueGraph: React.FC = () => {
    const [revenueData, setRevenueData] = useState<RevenueGraphDto[]>([]);
    const [userRevenueData, setUserRevenueData] = useState<RevenueDto[]>([]);
    const [selectedCoin, setSelectedCoin] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [userLoading, setUserLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [userError, setUserError] = useState<string>('');
    const [userFilter, setUserFilter] = useState<string>('');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Get default date range (last 7 days)
    const getDefaultDateRange = () => {
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 7);

        return {
            start: sevenDaysAgo.toISOString().split('T')[0],
            end: today.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        const defaultRange = getDefaultDateRange();
        setStartDate(defaultRange.start);
        setEndDate(defaultRange.end);
    }, []);

    const fetchRevenueData = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/pool/admin/revenue/graph', {
                params: {
                    startDate: startDate,
                    endDate: endDate
                }
            });

            const data: RevenueGraphDto[] = response.data || [];
            setRevenueData(data);

            // Set default selected coin to first available coin or 'all'
            if (data.length > 0 && selectedCoin === 'all') {
                setSelectedCoin('all');
            }
        } catch (err: any) {
            setError('Failed to fetch revenue data: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchUserRevenueData = async () => {
        if (!startDate || !endDate) return;

        setUserLoading(true);
        setUserError('');
        try {
            const response = await axios.get('/pool/admin/revenue/user', {
                params: {
                    startDate: startDate,
                    endDate: endDate
                }
            });

            const data: RevenueDto[] = response.data || [];
            setUserRevenueData(data);
        } catch (err: any) {
            setUserError('Failed to fetch user revenue data: ' + (err.response?.data?.message || err.message));
        } finally {
            setUserLoading(false);
        }
    };

    useEffect(() => {
        fetchRevenueData();
        fetchUserRevenueData();
    }, [startDate, endDate]);

    // Transform data for the chart
    const getChartData = (): CombinedData[] => {
        if (revenueData.length === 0) return [];

        // Get all unique dates
        const allDates = new Set<string>();
        revenueData.forEach(coinData => {
            coinData.data.forEach(item => allDates.add(item.date));
        });

        // Convert to array and sort
        const sortedDates = Array.from(allDates).sort();

        // Create combined data
        return sortedDates.map(date => {
            const dataPoint: CombinedData = { date };

            if (selectedCoin === 'all') {
                // Show all coins
                revenueData.forEach(coinData => {
                    const revenueItem = coinData.data.find(item => item.date === date);
                    dataPoint[coinData.coin] = revenueItem?.revenue || 0;
                });
            } else {
                // Show only selected coin
                const coinData = revenueData.find(item => item.coin === selectedCoin);
                if (coinData) {
                    const revenueItem = coinData.data.find(item => item.date === date);
                    dataPoint[selectedCoin] = revenueItem?.revenue || 0;
                }
            }

            return dataPoint;
        });
    };

    // Get available coins
    const availableCoins = revenueData.map(item => item.coin);

    // Generate colors for different coins
    const getLineColor = (coin: string, index: number) => {
        const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
        return colors[index % colors.length];
    };

    const handleDateRangeSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchRevenueData();
        fetchUserRevenueData();
    };

    // Filter users based on search term
    const filteredUserData = userRevenueData.filter(userData =>
        userData.user.toLowerCase().includes(userFilter.toLowerCase())
    );

    // Toggle row expansion
    const toggleRowExpansion = (user: string) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(user)) {
            newExpandedRows.delete(user);
        } else {
            newExpandedRows.add(user);
        }
        setExpandedRows(newExpandedRows);
    };

    // Calculate total revenue for a user
    const getUserTotalRevenue = (coinRevenue: CoinRevenueDto[]) => {
        return coinRevenue.reduce((sum, coin) => sum + coin.revenue, 0);
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Revenue Summary</h2>
            </div>

            {/* Date Range and Coin Filter */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <form onSubmit={handleDateRangeSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            Start Date
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            End Date
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="coinFilter" className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faCoins} className="mr-2" />
                            Coin Filter
                        </label>
                        <select
                            id="coinFilter"
                            value={selectedCoin}
                            onChange={(e) => setSelectedCoin(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                            <option value="all">All Coins</option>
                            {availableCoins.map((coin) => (
                                <option key={coin} value={coin}>
                                    {coin.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="sm:col-span-2 lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1 opacity-0">
                            Action
                        </label>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                        >
                            {loading ? 'Loading...' : 'Update Chart'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading revenue data...</span>
                </div>
            )}

            {/* Chart */}
            {!loading && revenueData.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4">
                    <div className="h-64 sm:h-80 lg:h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={getChartData()}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 20,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    stroke="#6b7280"
                                    fontSize={12}
                                    tick={{ fill: '#6b7280' }}
                                />
                                <YAxis
                                    stroke="#6b7280"
                                    fontSize={12}
                                    tick={{ fill: '#6b7280' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                    }}
                                />
                                <Legend />
                                {selectedCoin === 'all'
                                    ? availableCoins.map((coin, index) => (
                                        <Line
                                            key={coin}
                                            type="monotone"
                                            dataKey={coin}
                                            stroke={getLineColor(coin, index)}
                                            strokeWidth={2}
                                            dot={{ fill: getLineColor(coin, index), r: 4 }}
                                            name={coin.toUpperCase()}
                                        />
                                    ))
                                    : (
                                        <Line
                                            type="monotone"
                                            dataKey={selectedCoin}
                                            stroke="#3B82F6"
                                            strokeWidth={2}
                                            dot={{ fill: '#3B82F6', r: 4 }}
                                            name={selectedCoin.toUpperCase()}
                                        />
                                    )
                                }
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* No Data State */}
            {!loading && revenueData.length === 0 && (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={faChartLine} className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Revenue Data</h3>
                    <p className="text-gray-500">No revenue data available for the selected date range.</p>
                </div>
            )}

            {/* Summary Stats */}
            {!loading && revenueData.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
                    {(selectedCoin === 'all' ? availableCoins : [selectedCoin]).map((coin) => {
                        const coinData = revenueData.find(item => item.coin === coin);
                        const totalRevenue = coinData?.data.reduce((sum, item) => sum + item.revenue, 0) || 0;
                        const avgRevenue = coinData?.data.length ? totalRevenue / coinData.data.length : 0;

                        return (
                            <div key={coin} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-600">{coin.toUpperCase()}</h4>
                                        <p className="text-2xl font-bold text-blue-600">{totalRevenue.toFixed(8)}</p>
                                        <p className="text-xs text-gray-500">Avg: {avgRevenue.toFixed(8)}</p>
                                    </div>
                                    <FontAwesomeIcon icon={faCoins} className="w-8 h-8 text-blue-400" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* User Revenue Table */}
            <div className="mt-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">User Revenue Details</h3>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                    </div>
                </div>

                {/* User Error Message */}
                {userError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                        <div className="flex">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{userError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Loading State */}
                {userLoading && (
                    <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading user data...</span>
                    </div>
                )}

                {/* User Revenue Table */}
                {!userLoading && filteredUserData.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Revenue
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Coins
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredUserData.map((userData) => (
                                        <React.Fragment key={userData.user}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    <div className="truncate max-w-32 sm:max-w-none" title={userData.user}>
                                                        {userData.user}
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div className="text-xs sm:text-sm">
                                                        {getUserTotalRevenue(userData.coinRevenue).toFixed(8)}
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <span className="text-xs sm:text-sm">
                                                        {userData.coinRevenue.length} coins
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button
                                                        onClick={() => toggleRowExpansion(userData.user)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm px-2 py-1 rounded"
                                                    >
                                                        {expandedRows.has(userData.user) ? 'Hide' : 'Show'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedRows.has(userData.user) && (
                                                <tr>
                                                    <td colSpan={4} className="px-3 sm:px-6 py-4 bg-gray-50">
                                                        <div className="space-y-2">
                                                            <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Coin-wise Revenue:</h4>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                                                                {userData.coinRevenue.map((coinData) => (
                                                                    <div
                                                                        key={coinData.coin}
                                                                        className="bg-white rounded-lg p-2 sm:p-3 border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="min-w-0 flex-1">
                                                                                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                                                                                    {coinData.coin.toUpperCase()}
                                                                                </p>
                                                                                <p className="text-sm sm:text-lg font-bold text-blue-600 truncate" title={coinData.revenue.toFixed(8)}>
                                                                                    {coinData.revenue.toFixed(8)}
                                                                                </p>
                                                                            </div>
                                                                            <FontAwesomeIcon
                                                                                icon={faCoins}
                                                                                className="w-4 h-4 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0 ml-2"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* No User Data State */}
                {!userLoading && filteredUserData.length === 0 && userRevenueData.length > 0 && (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No users found matching your search.</p>
                    </div>
                )}

                {!userLoading && userRevenueData.length === 0 && (
                    <div className="text-center py-12">
                        <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No User Revenue Data</h3>
                        <p className="text-gray-500">No user revenue data available for the selected date range.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RevenueGraph;
